import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Department } from '../types';
import { api } from '../services/api';
import { Users, Plus, Save, X, Check, Loader2, UserPlus, FileText, Trash2, Search, Settings, Pencil, Copy } from 'lucide-react';
import DocumentActionModal from './DocumentActionModal';

interface AgencyTeamViewProps {
    user: User;
}

export const AgencyTeamView: React.FC<AgencyTeamViewProps> = ({ user }) => {
    const { t } = useTranslation('settings');
    const [members, setMembers] = useState<User[]>([]);
    const [clients, setClients] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);

    // Edit Member State
    const [editingMember, setEditingMember] = useState<User | null>(null);
    const [editFormData, setEditFormData] = useState({ name: '', phone: '', teamRole: '', departmentId: '' });
    const [isUpdating, setIsUpdating] = useState(false);

    // Departments State
    const [departments, setDepartments] = useState<Department[]>([]);
    const [isDepartmentsModalOpen, setIsDepartmentsModalOpen] = useState(false);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [editingDepartmentId, setEditingDepartmentId] = useState<string | null>(null);

    // New Member State
    const [newMember, setNewMember] = useState({ name: '', email: '', phone: '', teamRole: 'standard', departmentId: '' });
    const [createdMemberCredentials, setCreatedMemberCredentials] = useState<{ email: string, password: string } | null>(null);

    // Assignment State
    const [selectedMember, setSelectedMember] = useState<User | null>(null);
    const [assignedClientIds, setAssignedClientIds] = useState<string[]>([]);
    const [isSavingAssignments, setIsSavingAssignments] = useState(false);
    const [assignmentSearch, setAssignmentSearch] = useState('');

    // Modals
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        type: 'success' | 'error' | 'delete' | 'confirm';
        title: string;
        message: string;
        action?: () => void;
    }>({ isOpen: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        loadData();
    }, [user.id]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const agencyId = user.agencyId || user.id;

            // Parallel fetch
            const [allUsers, fetchedDepartments] = await Promise.all([
                api.getAgencyClients(user.id, undefined, true),
                api.getDepartments(agencyId)
            ]);

            setMembers(allUsers.filter(u => u.role === 'agency_member'));
            setClients(allUsers.filter(u => u.role === 'user'));
            setDepartments(fetchedDepartments);
        } catch (e) {
            console.error("Failed to load team data", e);
        } finally {
            setIsLoading(false);
        }
    };

    const openEditModal = (member: User) => {
        setEditingMember(member);
        setEditFormData({
            name: member.memberProfile?.name || '',
            phone: member.memberProfile?.phone || '',
            teamRole: member.memberProfile?.teamRole || 'standard',
            departmentId: member.memberProfile?.departmentId || ''
        });
    };

    const handleUpdateMember = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingMember) return;
        setIsUpdating(true);
        try {
            await api.updateAgencyMember(editingMember.id, editFormData);
            await loadData();
            setEditingMember(null);
            alert(t('team.modals.edit.success_message'));
        } catch (error: any) {
            console.error('Update Member Error:', error);
            alert(error.message);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCreateDepartment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newDepartmentName.trim()) return;

        try {
            const agencyId = user.agencyId || user.id;
            await api.createDepartment(agencyId, { name: newDepartmentName });
            setNewDepartmentName('');
            loadData(); // Reload to get new dept
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleDeleteDepartment = async (id: string) => {
        if (window.confirm(t('team.departments.delete_confirm'))) {
            try {
                await api.deleteDepartment(id);
                loadData();
            } catch (e: any) {
                alert(e.message);
            }
        }
    };

    const handleCreateMember = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            const { password } = await api.createAgencyMember(user.id, {
                ...newMember,
                departmentId: newMember.departmentId || undefined
            });

            // Refresh
            await loadData();
            setCreatedMemberCredentials({ email: newMember.email, password });
            setNewMember({ name: '', email: '', phone: '', teamRole: 'standard', departmentId: '' });
        } catch (e: any) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Creation Failed',
                message: e.message
            });
        } finally {
            setIsCreating(false);
        }
    };

    const confirmDeleteMember = (member: User) => {
        setModalConfig({
            isOpen: true,
            type: 'delete',
            title: t('team.modals.delete.title'),
            message: t('team.modals.delete.confirm', { name: member.memberProfile?.name || member.email }),
            action: () => handleDeleteMember(member.id)
        });
    };

    const handleDeleteMember = async (memberId: string) => {
        setIsLoading(true);
        setModalConfig(prev => ({ ...prev, isOpen: false })); // Close confirm
        try {
            const agencyId = user.agencyId || user.id;
            await api.deleteAgencyMember(memberId, agencyId);
            await loadData();
            setModalConfig({
                isOpen: true,
                type: 'success',
                title: t('team.modals.delete.success_title'),
                message: t('team.modals.delete.success_message')
            });
        } catch (e: any) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Deletion Failed',
                message: e.message
            });
            setIsLoading(false);
        }
    };

    const openAssignmentModal = (member: User) => {
        setSelectedMember(member);
        const currentAssignments = clients
            .filter(c => c.assignedAgentIds?.includes(member.id))
            .map(c => c.id);
        setAssignedClientIds(currentAssignments);
        setAssignmentSearch('');
    };

    const toggleAssignment = (clientId: string) => {
        setAssignedClientIds(prev =>
            prev.includes(clientId)
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const saveAssignments = async () => {
        if (!selectedMember) return;
        setIsSavingAssignments(true);
        try {
            const promises = clients.map(async (client) => {
                const shouldBeAssigned = assignedClientIds.includes(client.id);
                const isAssigned = client.assignedAgentIds?.includes(selectedMember.id);

                if (shouldBeAssigned && !isAssigned) {
                    const newIds = [...(client.assignedAgentIds || []), selectedMember.id];
                    await api.updateClientAssignments(client.id, newIds);
                } else if (!shouldBeAssigned && isAssigned) {
                    const newIds = (client.assignedAgentIds || []).filter(id => id !== selectedMember.id);
                    await api.updateClientAssignments(client.id, newIds);
                }
            });

            await Promise.all(promises);
            await loadData();
            setSelectedMember(null);
            alert(t('team.modals.assignment.success'));
        } catch (e: any) {
            alert('Failed to save assignments: ' + e.message);
        } finally {
            setIsSavingAssignments(false);
        }
    };

    const getAssignedClientCount = (memberId: string) => {
        return clients.filter(c => c.assignedAgentIds?.includes(memberId)).length;
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">{t('team.title')}</h2>
                    <p className="text-slate-500 text-sm mt-1">{t('team.subtitle')}</p>
                </div>
                <button
                    onClick={() => setIsDepartmentsModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition text-sm font-semibold"
                >
                    <Settings size={18} />
                    {t('team.departments.manage')}
                </button>
            </div>

            {/* Add Member Form */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-8">
                <form onSubmit={handleCreateMember} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.name')}</label>
                        <input
                            required
                            type="text"
                            value={newMember.name}
                            onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                            className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                            placeholder={t('team.placeholders.name')}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.email')}</label>
                        <input
                            required
                            type="email"
                            value={newMember.email}
                            onChange={e => setNewMember({ ...newMember, email: e.target.value })}
                            className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                            placeholder={t('team.placeholders.email')}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.phone')}</label>
                        <input
                            required
                            type="tel"
                            value={newMember.phone}
                            onChange={e => setNewMember({ ...newMember, phone: e.target.value })}
                            className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                            placeholder={t('team.placeholders.phone')}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.role')}</label>
                        <select
                            value={newMember.teamRole}
                            onChange={e => setNewMember({ ...newMember, teamRole: e.target.value })}
                            className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                        >
                            <option value="" disabled>{t('team.table.roles.select')}</option>
                            <option value="standard">{t('team.table.roles.standard')}</option>
                            <option value="financial_service">{t('team.table.roles.financial_service')}</option>
                            <option value="technical_service">{t('team.table.roles.technical_service')}</option>
                            <option value="asset_manager">{t('team.table.roles.asset_manager')}</option>
                            <option value="asset_user">{t('team.table.roles.asset_user')}</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.departments.title')}</label>
                        <select
                            value={newMember.departmentId}
                            onChange={e => setNewMember({ ...newMember, departmentId: e.target.value })}
                            className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                        >
                            <option value="">{t('team.departments.none')}</option>
                            {departments.map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <button disabled={isCreating} type="submit" className="w-full bg-black text-white px-4 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center justify-center gap-2">
                            {isCreating ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
                            {t('team.modals.add_member.add')}
                        </button>
                    </div>
                </form>
            </div>

            {/* Team List */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-6 py-4">{t('team.table.name')}</th>
                                <th className="px-6 py-4">{t('team.table.role')}</th>
                                <th className="px-6 py-4">{t('team.departments.title')}</th>
                                <th className="px-6 py-4 text-right">{t('team.table.assigned_clients')}</th>
                                <th className="px-6 py-4 text-right">{t('team.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {isLoading ? (
                                <tr><td colSpan={5} className="px-6 py-8 text-center"><Loader2 className="animate-spin text-slate-400" /></td></tr>
                            ) : members.length === 0 ? (
                                <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">{t('team.table.no_members')}</td></tr>
                            ) : members.map(member => (
                                <tr key={member.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-slate-900">{member.memberProfile?.name || 'Unknown'}</div>
                                        <div className="text-slate-500 text-xs">{member.email}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                            {t(`team.table.roles.${member.memberProfile?.teamRole || 'standard'}`)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        {member.memberProfile?.departmentId
                                            ? <span className="text-slate-700 font-medium">{departments.find(d => d.id === member.memberProfile?.departmentId)?.name || '-'}</span>
                                            : <span className="text-slate-400 italic">-</span>}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => openAssignmentModal(member)}
                                            className="text-blue-600 font-medium hover:underline"
                                        >
                                            {getAssignedClientCount(member.id)} {t('team.buttons.assign_clients')}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                                        <button
                                            onClick={() => openEditModal(member)}
                                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                            title="Edit Member"
                                        >
                                            <Pencil size={16} />
                                        </button>
                                        <button
                                            onClick={() => confirmDeleteMember(member)}
                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Departments Modal */}
            {isDepartmentsModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh]">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold">{t('team.departments.manage')}</h3>
                            <button onClick={() => setIsDepartmentsModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto">
                            {/* Create Dept Form */}
                            <form onSubmit={handleCreateDepartment} className="flex gap-2 mb-6">
                                <input
                                    type="text"
                                    placeholder={t('team.departments.name')}
                                    value={newDepartmentName}
                                    onChange={e => setNewDepartmentName(e.target.value)}
                                    className="flex-1 border border-slate-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-black"
                                />
                                <button type="submit" className="bg-black text-white px-4 py-2 rounded-xl font-bold hover:opacity-80">
                                    <Plus size={20} />
                                </button>
                            </form>

                            {/* Departments List */}
                            <div className="space-y-2">
                                {departments.length === 0 && (
                                    <p className="text-center text-slate-500 py-4 italic">{t('team.departments.none')}</p>
                                )}
                                {departments.map(dept => (
                                    <div key={dept.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                                        <span className="font-medium text-slate-900">{dept.name}</span>
                                        <button
                                            onClick={() => handleDeleteDepartment(dept.id)}
                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assignment Modal */}
            {selectedMember && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-900">{t('team.modals.assignment.title')} - {selectedMember.memberProfile?.name}</h3>
                            <button onClick={() => setSelectedMember(null)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <p className="text-sm text-slate-500 mb-4">{t('team.modals.assignment.subtitle')}</p>

                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
                                <input
                                    type="text"
                                    value={assignmentSearch}
                                    onChange={(e) => setAssignmentSearch(e.target.value)}
                                    placeholder={t('team.modals.assignment.search_placeholder')}
                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-black text-sm"
                                />
                            </div>

                            <div className="space-y-2">
                                {clients.filter(client => {
                                    if (!assignmentSearch) return true;
                                    const term = assignmentSearch.toLowerCase();
                                    const name = (client.profile?.companyName || client.config?.companyName || '').toLowerCase();
                                    const email = (client.email || '').toLowerCase();
                                    const vat = (client.profile?.vatNumber || '').toLowerCase();
                                    return name.includes(term) || email.includes(term) || vat.includes(term);
                                }).map(client => {
                                    const isSelected = assignedClientIds.includes(client.id);
                                    const clientName = client.profile?.companyName || client.config?.companyName || client.email;
                                    const vat = client.profile?.vatNumber;

                                    return (
                                        <div key={client.id}
                                            onClick={() => toggleAssignment(client.id)}
                                            className={`p-3 rounded-xl border cursor-pointer transition flex items-center justify-between group ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                                        >
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-white'}`}>
                                                    {isSelected && <Check size={12} className="text-white" />}
                                                </div>
                                                <div className="truncate">
                                                    <span className={`block font-bold truncate ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>{clientName}</span>
                                                    {vat && <span className="text-xs text-slate-400 font-mono">VAT: {vat}</span>}
                                                </div>
                                            </div>
                                            <span className="text-xs text-slate-400 shrink-0 ml-2">{client.email}</span>
                                        </div>
                                    );
                                })}
                                {clients.filter(client => {
                                    if (!assignmentSearch) return true;
                                    const term = assignmentSearch.toLowerCase();
                                    const name = (client.profile?.companyName || client.config?.companyName || '').toLowerCase();
                                    const email = (client.email || '').toLowerCase();
                                    const vat = (client.profile?.vatNumber || '').toLowerCase();
                                    return name.includes(term) || email.includes(term) || vat.includes(term);
                                }).length === 0 && (
                                        <div className="text-center py-8 text-slate-400 italic">No branches found matching "{assignmentSearch}"</div>
                                    )}
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                            <button onClick={() => setSelectedMember(null)} className="px-5 py-2.5 rounded-xl font-bold text-slate-500 hover:bg-slate-50">{t('team.modals.assignment.cancel')}</button>
                            <button onClick={saveAssignments} disabled={isSavingAssignments} className="bg-black text-white px-6 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2">
                                {isSavingAssignments && <Loader2 className="animate-spin" size={18} />}
                                {t('team.modals.assignment.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Member Modal */}
            {editingMember && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-900">{t('team.modals.edit.title')}</h3>
                            <button onClick={() => setEditingMember(null)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                        </div>
                        <form onSubmit={handleUpdateMember} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.name')}</label>
                                <input
                                    required
                                    type="text"
                                    value={editFormData.name}
                                    onChange={e => setEditFormData({ ...editFormData, name: e.target.value })}
                                    className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.phone')}</label>
                                <input
                                    type="tel"
                                    value={editFormData.phone}
                                    onChange={e => setEditFormData({ ...editFormData, phone: e.target.value })}
                                    className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.role')}</label>
                                <select
                                    value={editFormData.teamRole}
                                    onChange={e => setEditFormData({ ...editFormData, teamRole: e.target.value })}
                                    className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                                >
                                    {['standard', 'financial_service', 'technical_service', 'asset_manager', 'asset_user'].map(role => (
                                        <option key={role} value={role}>{t(`team.table.roles.${role}`)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.departments.select')}</label>
                                <select
                                    value={editFormData.departmentId}
                                    onChange={e => setEditFormData({ ...editFormData, departmentId: e.target.value })}
                                    className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                                >
                                    <option value="">{t('team.departments.none')}</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={() => setEditingMember(null)} className="px-5 py-2.5 rounded-xl font-bold text-slate-500 hover:bg-slate-50">
                                    {t('team.modals.add_member.cancel')}
                                </button>
                                <button type="submit" disabled={isUpdating} className="bg-black text-white px-6 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2">
                                    {isUpdating && <Loader2 className="animate-spin" size={18} />}
                                    {t('account.update')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Created Member Credentials Modal */}
            {createdMemberCredentials && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl text-center">
                        <div className="bg-green-50 p-6 border-b border-green-100 flex flex-col items-center">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-3">
                                <Check size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-green-900">{t('team.modals.add_member.success_title') || 'Member Added!'}</h3>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="text-left bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <p className="text-xs font-bold text-slate-400 uppercase mb-1">Email</p>
                                <p className="text-lg font-bold text-slate-900">{createdMemberCredentials.email}</p>
                            </div>
                            <div className="text-left bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <p className="text-xs font-bold text-slate-400 uppercase mb-1">Temporary Password</p>
                                <div className="flex items-center justify-between">
                                    <p className="text-lg font-mono font-bold text-slate-900">{createdMemberCredentials.password}</p>
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(createdMemberCredentials.password); alert("Copied!") }}
                                        className="text-blue-600 hover:bg-blue-50 p-2 rounded"
                                    >
                                        <Copy size={16} />
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-slate-500">
                                Please share these credentials with the team member. They will not be shown again.
                            </p>
                        </div>
                        <div className="p-4 bg-slate-50 border-t border-slate-100">
                            <button
                                onClick={() => setCreatedMemberCredentials(null)}
                                className="w-full bg-black text-white py-3 rounded-xl font-bold hover:opacity-80 transition"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <DocumentActionModal
                isOpen={modalConfig.isOpen}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={modalConfig.action}
                isLoading={isLoading && modalConfig.type === 'delete'}
            />
        </div>
    );
};
